import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ClipboardList, PackagePlus, RefreshCw } from 'lucide-react';

import StockManagerLayout from '../components/StockManagerLayout';
import CreateDispatchOrderSection from '../components/CreateDispatchOrderSection';
import DispatchOrdersSection from '../components/DispatchOrdersSection';
import DispatchHistoryReport from '../components/DispatchHistoryReport';
import { DISPATCH_DESTINATIONS, groupDispatchDestinations } from '../utils/dispatchDestinations';

import {
  fetchDispatchStakeholders,
  fetchWarehouses,
  fetchOutlets,
  fetchInventoryProducts,
  fetchInventoryDispatchOrders,
  fetchCatalogBarcodes,
  fetchRatePlans,
  fetchUnits,
} from '../features/inventory/stockManagerInventorySlice';

const DispatchPage = () => {
  const dispatch = useDispatch();
  const [activePanel, setActivePanel] = useState('create');

  const { userInfo } = useSelector((state) => state.posUser || {});

  const {
    inventoryProducts = [],
    dispatchStakeholders = [],
    warehouses = [],
    outlets = [],
    catalogBarcodes = [],
    ratePlans = [],
    units = [],
    inventoryDispatchOrders = [],
    inventoryDispatchLoading = false,
    inventoryDispatchError = null,
    inventoryDispatchSuccess = null,
  } = useSelector((state) => state.stockManagerInventory || {});

  useEffect(() => {
    if (userInfo?.token) {
      dispatch(fetchInventoryProducts());
      dispatch(fetchDispatchStakeholders());
      dispatch(fetchWarehouses());
      dispatch(fetchOutlets());
      dispatch(fetchCatalogBarcodes());
      dispatch(fetchRatePlans());
      dispatch(fetchUnits());
      dispatch(fetchInventoryDispatchOrders());
    }
  }, [dispatch, userInfo?.token]);

  const refreshDispatchData = useCallback(async () => {
    if (!userInfo?.token) return;

    await Promise.all([
      dispatch(fetchInventoryProducts()),
      dispatch(fetchCatalogBarcodes()),
      dispatch(fetchInventoryDispatchOrders()),
    ]);
  }, [dispatch, userInfo?.token]);

  const dispatchStats = useMemo(() => {
    const completedStatuses = new Set([
      'received_to_outlet',
      'received_by_stakeholder',
      'dispatched',
    ]);

    return inventoryDispatchOrders.reduce(
      (acc, order) => {
        const status = String(order.dispatch_status || '').toLowerCase();
        acc.total += 1;
        if (status === 'draft') acc.draft += 1;
        if (completedStatuses.has(status)) acc.completed += 1;
        if (!completedStatuses.has(status) && status !== 'cancelled') acc.active += 1;
        return acc;
      },
      { total: 0, draft: 0, active: 0, completed: 0 }
    );
  }, [inventoryDispatchOrders]);

  const ordersByDestination = useMemo(
    () => groupDispatchDestinations(inventoryDispatchOrders),
    [inventoryDispatchOrders]
  );
  const selectedDestination = DISPATCH_DESTINATIONS.find(({ id }) => id === activePanel);
  const linkedDispatchId = new URLSearchParams(window.location.search).get('dispatchOrderId') || '';
  const openedLinkedDispatch = React.useRef('');
  useEffect(() => {
    if (!linkedDispatchId || openedLinkedDispatch.current === linkedDispatchId) return;
    const destination = DISPATCH_DESTINATIONS.find(({ id }) => ordersByDestination[id]
      .some(order => String(order.id) === linkedDispatchId));
    if (destination) {
      openedLinkedDispatch.current = linkedDispatchId;
      setActivePanel(destination.id);
    }
  }, [linkedDispatchId, ordersByDestination]);

  const ViewCard = ({ id, icon: Icon, label, value, detail }) => (
    <button
      type="button"
      aria-pressed={activePanel === id}
      onClick={() => setActivePanel(id)}
      className={`rounded-lg border p-4 text-left shadow-sm transition ${
        activePanel === id
          ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100'
          : 'bg-white hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-500">{label}</div>
          <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
        </div>
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${
            activePanel === id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
          }`}
        >
          <Icon size={20} />
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-500">{detail}</div>
    </button>
  );

  return (
    <StockManagerLayout>
      <main className="space-y-4 p-4">
        <div className="rounded-xl border bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Inventory Dispatch</h1>
              <p className="mt-1 text-sm text-gray-500">
                Create stock movements and track every dispatch until it is received.
              </p>
            </div>
            <div className="flex flex-col gap-2 md:min-w-[480px] md:flex-row md:items-center md:justify-end">
              <button
                type="button"
                onClick={refreshDispatchData}
                disabled={inventoryDispatchLoading}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw
                  size={16}
                  className={inventoryDispatchLoading ? 'animate-spin' : ''}
                />
                {inventoryDispatchLoading ? 'Refreshing' : 'Refresh'}
              </button>
              <div className="grid grid-cols-3 gap-2 text-center text-xs md:min-w-[360px]">
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="font-bold text-gray-900">{dispatchStats.active}</div>
                  <div className="text-gray-500">Active</div>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="font-bold text-gray-900">{dispatchStats.draft}</div>
                  <div className="text-gray-500">Draft</div>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="font-bold text-gray-900">{dispatchStats.completed}</div>
                  <div className="text-gray-500">Done</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {inventoryDispatchError && (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
            {inventoryDispatchError}
          </div>
        )}

        {inventoryDispatchSuccess && (
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            {inventoryDispatchSuccess}
          </div>
        )}

        <section className="rounded-xl border bg-slate-50 p-4 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <ViewCard
              id="create"
              icon={PackagePlus}
              label="Create Dispatch"
              value="New"
              detail="Select warehouse, destination and products to dispatch."
            />
            {DISPATCH_DESTINATIONS.filter(({ id, optional }) =>
              !optional || ordersByDestination[id].length > 0 || activePanel === id
            ).map(({ id, label }) => (
              <ViewCard
                key={id}
                id={id}
                icon={ClipboardList}
                label={`${label} Orders`}
                value={ordersByDestination[id].length}
                detail="View & track orders"
              />
            ))}
            <ViewCard id="history" icon={ClipboardList} label="Dispatch History" value="Reports" detail="Filter by date, stakeholder, outlet, product and status." />
          </div>
        </section>

        {activePanel === 'history' ? <DispatchHistoryReport token={userInfo?.token} /> : activePanel === 'create' ? (
          <CreateDispatchOrderSection
            inventoryProducts={inventoryProducts}
            dispatchStakeholders={dispatchStakeholders}
            warehouses={warehouses}
            outlets={outlets}
            catalogBarcodes={catalogBarcodes}
            ratePlans={ratePlans}
            units={units}
            loading={inventoryDispatchLoading}
          />
        ) : (
          <section aria-label={`${selectedDestination.label} order tracking`}>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">
              View & Track {selectedDestination.label} Orders
            </h2>
            <DispatchOrdersSection
              initialDispatchOrderId={linkedDispatchId}
              key={activePanel}
              orders={ordersByDestination[activePanel]}
              loading={inventoryDispatchLoading}
              catalogBarcodes={catalogBarcodes}
              onRefresh={refreshDispatchData}
              inventoryProducts={inventoryProducts}
              onCreate={() => setActivePanel('create')}
            />
          </section>
        )}
      </main>
    </StockManagerLayout>
  );
};

export default DispatchPage;
